import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { TbDiscount } from 'react-icons/tb';

import cn from '@/lib/clsxm';
import { APIErrorResponseGenerator } from '@/lib/helper';
import { eventTracker } from '@/lib/service-workers';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { Input } from '@/components/@base/input';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import {
  useDeleteVoucher,
  useGetCart,
  usePostVoucher,
} from '@/components/app/cart/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import ICON_SIZE, { ICON_COLOR } from '@/constant/icon-size-color';
import { pageLevelLocalization } from '@/constant/localization';
import { GET_CART } from '@/constant/query-key';

const { cart: cartLocalization } = pageLevelLocalization;

export default function Discount() {
  const { mutate: addCode, isPending: addCodePending } = usePostVoucher();
  const { mutate: removeCode, isPending: removeCodePending } = useDeleteVoucher();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetCart();
  const { profile } = useGetProfile();
  const [code, setCode] = useState('');
  useEffect(() => {
    if (!code && data) {
      setCode(data.voucher.code);
    }
  }, [data]);
  const voucherExist = !!data?.voucher?.code;
  const handleSubmitCode = () => {
    if (!code) {
      showToast({ message: cartLocalization.enterYourCode, type: 'error' });
    } else if (voucherExist) {
      removeCode(data?.voucher?.code, {
        onSuccess: (res) => {
          showToast({ message: cartLocalization.voucherSuccessfullyRemoved, type: 'success' });

          queryClient.setQueryData([GET_CART], res.payload);
          setCode('');
        },
        onError: (e) => {
          if (e instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(e));
          }
        },
      });
    } else
      addCode(code, {
        onSuccess: (res) => {
          queryClient.setQueryData([GET_CART], res.payload);
          showToast({ message: cartLocalization.voucherSuccessfullyAdded, type: 'success' });
          eventTracker(profile?.mobile, 'coupon_applied', new Date().toISOString(), {
            code: code,
          });
        },
        onError: (e) => {
          if (e instanceof AxiosError) {
            setCode('');
            showToast(APIErrorResponseGenerator(e));
          }
        },
      });
  };
  if (data?.products?.length === 0 || isLoading) {
    return null;
  }

  return (
    <Container className='border-t-[1px] border-t-[#F4F5F6] px-[15px] pt-[30px] md:pb-5'>
      <Container center className='justify-start gap-2'>
        <TbDiscount color={ICON_COLOR.primary} size={ICON_SIZE.lg} />
        <Text className='text-[#424244]' bold>
          {cartLocalization.doYouHaveDiscount}
        </Text>
      </Container>
      <Container center className='mt-3 w-full gap-[7px] md:justify-start'>
        <Input
          placeholder={cartLocalization.discount}
          className='h-[45px] rounded-md border-[#E5E8EB] placeholder:text-[14px] placeholder:text-[#C1C3C8]'
          containerClassName='w-full md:max-w-[216px]'
          onChange={(e) => setCode(e.target.value)}
          value={data?.voucher?.code || code}
          disabled={voucherExist || false}
        />
        <Button
          isLoading={addCodePending || removeCodePending}
          className={cn(
            'h-[45px] w-20 bg-[#F0F8FF] text-[#6097F3]',
            voucherExist && 'bg-[#FFF1ED] text-[#FF6437]',
          )}
          onClick={handleSubmitCode}
        >
          {voucherExist ? cartLocalization.removeVoucher : cartLocalization.submitCode}
        </Button>
      </Container>
    </Container>
  );
}
